export type Regime = "tcf_eu" | "tcf_uk" | "ch" | "gpp_us" | "notice_only" | "none";

export type CustomVendor = {
  key: string;
  name: string;
  privacyPolicyUrl?: string;
  domains: string[];
  cookiePatterns: string[];
  purposes: string[];
  purposeKeys?: string[];
  legalBasis: string;
};

export type GvlPurpose = {
  id: number;
  name: string;
  description?: string;
  illustrations?: string[];
};

export type GvlVendor = {
  id: number;
  name: string;
  purposes: number[];
  legIntPurposes: number[];
  flexiblePurposes?: number[];
  specialPurposes?: number[];
  features?: number[];
  specialFeatures?: number[];
  policyUrl?: string;
  legIntClaimUrl?: string;
  urls?: Array<{ langId?: string; privacy?: string; legIntClaim?: string }>;
  cookieMaxAgeSeconds?: string | number | null;
  usesCookies?: boolean;
  cookieRefresh?: boolean;
  usesNonCookieAccess?: boolean;
  deviceStorageDisclosureUrl?: string;
  deviceStorageDisclosure?: unknown;
  dataRetention?: { stdRetention?: number; purposes?: Record<string, number>; specialPurposes?: Record<string, number> };
  dataDeclaration?: number[];
};

export type PublisherRestriction = {
  vendor_id: number;
  purpose_id: number;
  restriction_type: string;
};

export type CmpConfigPayload = {
  propertyKey: string;
  configVersion: number;
  publishedAt?: string;
  language: string;
  country?: string | null;
  regime: Regime;
  regimesEnabled?: string[];
  cmpId: number;
  cmpVersion: number;
  sdkName?: string;
  publisherCC?: string;
  purposeOneTreatment?: boolean;
  consentMaxAgeDays?: number;
  consentMaxAgeDefaultDays?: number;
  publisherRestrictions?: PublisherRestriction[];
  banner: { ui: Record<string, string>; texts: Record<string, string> };
  vendorIds: number[];
  purposeIds: number[];
  specialFeatureIds: number[];
  stackIds?: number[];
  customVendors: CustomVendor[];
  customPurposes: Array<{ key: string; name: string; necessary?: boolean }>;
  gcm?: {
    wait_for_update?: number;
    default_outside_opt_in?: string;
    mapping?: Record<string, string[] | string>;
  };
  trackerDomainMap?: Record<string, string>;
  gvl: {
    vendorListVersion: number;
    tcfPolicyVersion?: number;
    purposes: GvlPurpose[];
    specialPurposes?: GvlPurpose[];
    features?: GvlPurpose[];
    specialFeatures: GvlPurpose[];
    stacks?: Array<{ id: number; name: string; description?: string; purposes: number[]; specialFeatures: number[] }>;
    dataCategories?: Array<{ id: number; name: string; description?: string }>;
    /** TCF v2.4 GVL standardTexts (localized) */
    standardTexts?: { features?: string };
    vendors: GvlVendor[];
  };
};

export type ConsentState = {
  purposeConsents: Record<number, boolean>;
  purposeLegitimateInterests: Record<number, boolean>;
  vendorConsents: Record<number, boolean>;
  vendorLegitimateInterests: Record<number, boolean>;
  specialFeatureOptins: Record<number, boolean>;
  customVendors: Record<string, boolean>;
  customPurposes: Record<string, boolean>;
};

export type ConsentSnapshot = {
  regime: Regime;
  purposes: Record<number, boolean>;
  vendors: Record<number, boolean>;
  /** true = legitimate interest not objected to (TCF purposeLegitimateInterests). */
  purposeLegitimateInterests: Record<number, boolean>;
  /** true = legitimate interest not objected to (TCF vendorLegitimateInterests). */
  vendorLegitimateInterests: Record<number, boolean>;
  customVendors: Record<string, boolean>;
  tcString: string | null;
  gppString: string | null;
  configVersion: number;
  language: string;
};

export type CmpEvent = "consent" | "ready" | "shown" | "hidden";

/** Purposes that cannot use legitimate interest under TCF v2.2 */
export const NO_LI_PURPOSE_IDS = new Set([1, 3, 4, 5, 6]);

/** Bump to force re-surfacing after changes to what is disclosed/signalled. */
export const CONSENT_SCHEMA_VERSION = 2;
